import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Button } from '@/components/shared-components/BUI/Button'
import { Modal } from '@/components/shared-components/BUI/Modal'
import { useLocale } from '@/context/localeContext'

import { buildPassTheme } from 'src/styles/theme'

type Config = {
  title: string
  message: React.ReactNode
  confirmText: React.ReactNode
  cancelText: string
  isDestructive: boolean
  size: keyof (typeof buildPassTheme)['modal']
}

type ConfirmContextValue = {
  showConfirm: (customConfig?: Partial<Config>) => Promise<boolean>
}

export type ConfirmConfig = Partial<Config>

const DEFAULT_CONFIG: Config = {
  title: 'Confirm',
  message: 'Are you sure?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  isDestructive: false,
  size: 'small',
}

const ConfirmContext = createContext<ConfirmContextValue>({
  showConfirm: () => Promise.resolve(false),
})

type Props = {
  children: React.ReactNode
}

export const ConfirmProvider: React.FC<Props> = ({ children }) => {
  const { t } = useLocale()
  const confirmRef = useRef<(confirm: boolean) => void>(undefined)

  const [open, setOpen] = useState(false)
  const [config, setConfig] = useState<ConfirmConfig>(DEFAULT_CONFIG)

  const showConfirm = useCallback(
    async (customConfig: Partial<Config> = {}) => {
      return new Promise<boolean>((resolve) => {
        confirmRef.current = resolve
        setConfig({ ...DEFAULT_CONFIG, ...customConfig })
        setOpen(true)
      })
    },
    []
  )

  const onConfirm = () => {
    confirmRef.current?.(true)
    setOpen(false)
  }

  const onCancel = () => {
    confirmRef.current?.(false)
    setOpen(false)
  }

  const value = useMemo(() => ({ showConfirm }), [showConfirm])

  const getConfirmButtonText = () => {
    const translatedConfirmText = t(config.confirmText as string)
    if (config.confirmText && translatedConfirmText) {
      return translatedConfirmText
    } else if (config.confirmText) {
      return config.confirmText
    } else {
      return 'Confirm'
    }
  }

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        destroyOnClose
        title={t(config.title)}
        open={open}
        onCancel={onCancel}
        footerLayout="spaced"
        width={buildPassTheme.modal[config.size]}
        maskStyle={{ zIndex: 1050 }} // on top of other modals - gross, avoid this pls
        footer={
          <>
            <Button onClick={onCancel}>{t(config.cancelText)}</Button>
            <Button
              variant="primary"
              negative={config.isDestructive}
              onClick={onConfirm}
            >
              {getConfirmButtonText()}
            </Button>
          </>
        }
      >
        {typeof config.message === 'string'
          ? t(config.message as string)
          : config.message}
      </Modal>
    </ConfirmContext.Provider>
  )
}

export const useConfirm = () => useContext(ConfirmContext)
